'use client';
import type { CSSProperties, ReactNode } from 'react';
import { GraphNodeShapeKind, NODE_FILL, SELECTED_SILHOUETTE_COLOR, TRANSITION_DURATION, boxShadowGlowToDropShadowFilter, type GraphNodeShapeDescriptor, } from './graph-node-frame-constants';
import { ButtonPreset } from '@/constants/componentPresets';
import { Cbutton } from '../Cbutton';
/**
 * GraphNodeFrame — the reusable node chrome shared by the mindmap and flowchart.
 *
 * It renders a node's silhouette (one of three strategies — see
 * {@link GraphNodeShapeKind}) with the depth/selection box-shadow on the
 * correct element so the **selection glow and accent border follow the actual
 * shape**, never a rectangle behind a non-rect shape:
 *
 *  - BorderRadius → a single rounded element; `boxShadow` (depth + glow) sits on
 *    it directly and the selection is a real 1px border. The rectangle (or
 *    rounded rect) IS the silhouette, so a box-shadow already follows it.
 *    (mindmap rounded card)
 *  - ClipPath → a two-layer clip-path: an accent/border layer with a `fill`
 *    inset on top, both clipped to the same path; the glow is recast as
 *    `filter: drop-shadow(...)` on the wrapper so the halo traces the clipped
 *    alpha edge instead of the wrapper's bounding box. (mindmap hexagon)
 *  - SvgBody → an inline-SVG body drawn behind the content; the SVG owns its
 *    stroke so the border follows the drawn path, and the glow is likewise a
 *    `filter: drop-shadow(...)` so it hugs the drawn body. (flowchart
 *    cylinder/document)
 *
 * The host always composes the glow as a `box-shadow` value; for the two
 * non-rectangular silhouettes the frame converts that glow into the equivalent
 * `drop-shadow` filter via {@link boxShadowGlowToDropShadowFilter}.
 *
 * Every piece of interaction state is an explicit prop — the frame closes over
 * nothing. The host node component owns hover/select/dim/entrance state and the
 * box-shadow math, and passes the resolved values in. The frame only positions
 * them onto the right element for the chosen silhouette.
 */
export interface GraphNodeFrameProps {
    /** Which silhouette to draw, and its geometry. */
    shape: GraphNodeShapeDescriptor;
    /** Accent/border color for the silhouette's outline (used by ClipPath + SvgBody). */
    accentColor: string;
    /** Interior fill. Defaults to {@link NODE_FILL}. The mindmap root passes its radial gradient. */
    fill?: string;
    /** Border thickness for the ClipPath inset (px). Ignored for BorderRadius/SvgBody. */
    borderWidth?: number;
    /** Whether the node is selected (drives the white silhouette + glow placement). */
    isSelected: boolean;
    /**
     * Fully-resolved box-shadow for this node. The frame places it on the correct
     * element for the silhouette (the shape div for BorderRadius; the outer
     * wrapper for ClipPath/SvgBody). The host composes depth + cloud + selection.
     */
    boxShadow?: string;
    /** Opacity for the whole node (1, or the dimmed value when off the active path). */
    opacity: number;
    /** CSS transform for hover scale + entrance offset. */
    transform: string;
    /** CSS transition list for interaction/entrance. */
    transition: string;
    /** Cursor (pointer when interactive, default otherwise). */
    cursor: CSSProperties['cursor'];
    /** Padding for the content layer. Defaults to `0 14px`. */
    contentPadding?: CSSProperties['padding'];
    /**
     * Node pixel width/height, forwarded to a SvgBody's `renderBody` so it can do
     * path math. Only needed for the SvgBody silhouette (cylinder/document); the
     * BorderRadius/ClipPath silhouettes fill their parent and ignore these.
     */
    width?: number;
    height?: number;
    /**
     * Accessible name for the node. `Cbutton` is a div, not a native `<button>`:
     * it only takes `role="button"`, `tabIndex={0}` and Enter/Space activation
     * when it has both a click handler and an accessible name. Clickable hosts
     * MUST pass the node's own text here or the node is keyboard-inert. Nothing
     * is derived automatically — the host owns the wording.
     */
    ariaLabel?: string;
    /** Label / decoration rendered on top of the silhouette. */
    children: ReactNode;
    /** Connection handles (react-flow `<Handle>`s) rendered alongside the content. */
    handles?: ReactNode;
    onClick?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}
const WRAPPER_BASE_STYLE: CSSProperties = {
    width: '100%',
    height: '100%',
    position: 'relative',
    userSelect: 'none',
};
const CONTENT_LAYER_STYLE: CSSProperties = {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};
const SVG_BODY_LAYER_STYLE: CSSProperties = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
};
const CSS_GRADIENT_MARKER = 'gradient(';
const TRANSPARENT_BACKGROUND_COLOR = 'transparent';
const NO_BACKGROUND_IMAGE = 'none';
/**
 * The clip-path/svg silhouettes cast their glow as a `filter: drop-shadow(...)`
 * rather than the wrapper `box-shadow` the host's transition list animates. Add
 * a matching `filter` transition (same duration) so the glow fades in/out on
 * select instead of snapping, regardless of what the host passed in.
 */
function withFilterTransition(transition: string): string {
    return `${transition}, filter ${TRANSITION_DURATION} ease-out`;
}
/**
 * `fill` is a CSS background value: a flat color for most nodes, but the mindmap
 * root passes a radial gradient. Only `background-image` paints a gradient, so
 * route each form to the longhand that renders it, and always emit BOTH
 * longhands so the background family stays stable across renders (the
 * `background` shorthand is banned by `local-rules/no-background-style-shorthand`).
 */
function buildFillBackground(fill: string): Pick<CSSProperties, 'backgroundColor' | 'backgroundImage'> {
    if (fill.includes(CSS_GRADIENT_MARKER)) {
        return { backgroundColor: TRANSPARENT_BACKGROUND_COLOR, backgroundImage: fill };
    }
    return { backgroundColor: fill, backgroundImage: NO_BACKGROUND_IMAGE };
}
export function GraphNodeFrame({ shape, accentColor, fill = NODE_FILL, borderWidth = 1.5, isSelected, boxShadow, opacity, transform, transition, cursor, contentPadding = '0 14px', width = 0, height = 0, ariaLabel, children, handles, onClick, onMouseEnter, onMouseLeave, }: GraphNodeFrameProps) {
    if (shape.kind === GraphNodeShapeKind.BorderRadius) {
        // Single rounded element: background is the fill, depth + glow live in
        // boxShadow, selection is a real 1px white border. (mindmap rounded card)
        return (
        <Cbutton rootClassName="nopan nodrag" preset={ButtonPreset.Ghost} padding={0} ariaLabel={ariaLabel} onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} disableHoverEffects={cursor !== 'pointer'} disableClickMotion style={WRAPPER_BASE_STYLE} containerStyle={{
                ...WRAPPER_BASE_STYLE,
                ...buildFillBackground(fill),
                border: isSelected ? `1px solid ${SELECTED_SILHOUETTE_COLOR}` : 'none',
                borderRadius: shape.borderRadius,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: contentPadding,
                boxSizing: 'border-box',
                transform,
                transition,
                opacity,
                boxShadow,
            }}>
          {children}
          {handles}
        </Cbutton>
      );
    }
    if (shape.kind === GraphNodeShapeKind.ClipPath) {
        // Two-layer clip-path: accent layer + fill inset on top, both clipped to the
        // same silhouette so the border follows the shape. The glow is a
        // drop-shadow filter (not box-shadow) so the halo follows the clipped alpha
        // edge instead of the rectangular wrapper.
        return (
        <Cbutton rootClassName="nopan nodrag" preset={ButtonPreset.Ghost} padding={0} ariaLabel={ariaLabel} onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} disableHoverEffects={cursor !== 'pointer'} disableClickMotion style={WRAPPER_BASE_STYLE} containerStyle={{
                ...WRAPPER_BASE_STYLE,
                opacity,
                transform,
                transition: withFilterTransition(transition),
                filter: boxShadowGlowToDropShadowFilter(boxShadow),
            }}>
          <div style={{
                position: 'absolute',
                inset: 0,
                clipPath: shape.clipPath,
                backgroundColor: isSelected ? SELECTED_SILHOUETTE_COLOR : accentColor,
            }}/>
          <div style={{
                position: 'absolute',
                inset: isSelected ? 1 : borderWidth,
                clipPath: shape.clipPath,
                ...buildFillBackground(fill),
            }}/>
          <div style={{ ...CONTENT_LAYER_STYLE, padding: contentPadding }}>
            {children}
          </div>
          {handles}
        </Cbutton>
      );
    }
    // SvgBody: an inline-SVG body drawn behind the content; the SVG strokes its
    // own outline so the border follows the path exactly. The glow is a
    // drop-shadow filter (not box-shadow) so the halo hugs the drawn body instead
    // of the rectangular wrapper.
    const svgBodyArgs = {
        width,
        height,
        accentColor,
        fill,
        isSelected,
    };
    return (
      <Cbutton rootClassName="nopan nodrag" preset={ButtonPreset.Ghost} padding={0} ariaLabel={ariaLabel} onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} disableHoverEffects={cursor !== 'pointer'} disableClickMotion style={WRAPPER_BASE_STYLE} containerStyle={{
            ...WRAPPER_BASE_STYLE,
            opacity,
            transform,
            transition: withFilterTransition(transition),
            filter: boxShadowGlowToDropShadowFilter(boxShadow),
        }}>
        <div style={SVG_BODY_LAYER_STYLE}>
          {shape.renderBody(svgBodyArgs)}
        </div>
        <div style={{ ...CONTENT_LAYER_STYLE, padding: contentPadding }}>
          {children}
        </div>
        {handles}
      </Cbutton>
    );
}
